import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FileZipOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Form, Popover, Row, Space, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { ContentBlockerField, EContentBlockerVisualType, ERcbTier, EUserRole, TRANSLATABLE_CONTENT_BLOCKER_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { FormFieldContentBlockerTemplateConnectedServices } from "./fields/connected-services.js";
import { FormFieldContentBlockerTemplateDescription } from "./fields/description.js";
import { FormFieldContentBlockerTemplateExampleEmbedCodeList } from "./fields/example-embed-codes.js";
import { FormFieldContentBlockerTemplateExtendsIdentifier } from "./fields/extends-identifier.js";
import { FormFieldContentBlockerTemplateIsVisualDarkMode } from "./fields/is-visual-dark-mode.js";
import { FormFieldContentBlockerTemplateIsVisual } from "./fields/is-visual.js";
import { FormFieldContentBlockerTemplateRules } from "./fields/rule-form.js";
import { FormFieldContentBlockerTemplateRuleGroups } from "./fields/rule-group-form.js";
import { FormFieldServiceTemplateRuleNotice } from "./fields/rule-notice.js";
import { FormFieldContentBlockerTemplateShouldForceToShowVisual } from "./fields/should-force-to-show-visual.js";
import { FormFieldContentBlockerTemplateVisualBlur } from "./fields/visual-blur.js";
import { FormFieldContentBlockerTemplateVisualContentType, VISUAL_CONTENT_HERO_TYPES } from "./fields/visual-content-type.js";
import { FormFieldContentBlockerTemplateVisualHeroButtonText } from "./fields/visual-hero-button-text.js";
import { FormFieldContentBlockerTemplateVisualType } from "./fields/visual-type.js";
import { useContentBlockerTemplateById } from "../../../../../hooks/content-blocker/useContentBlockerTemplate.js";
import { useContentBlockerTemplateMetaData } from "../../../../../hooks/content-blocker/useContentBlockerTemplateMetaData.js";
import { useFormEvent } from "../../../../../hooks/form/useFormEvent.js";
import { useShouldVersionIncrease } from "../../../../../hooks/useShouldVersionIncrease.js";
import { useTranslation } from "../../../../../others/i18n.js";
import { useStores } from "../../../../../store/root.js";
import { colors } from "../../../../../styles/colors.js";
import { spacing, templateFormDefaultProps } from "../../../../../styles/spacing.js";
import { EFormEvent } from "../../../../../util/eventBus.js";
import { prepareExportDataContentBlocker, prepareImportDataContentBlocker } from "../../../../../util/export-import.js";
import { areExtendedFieldsHidden, getInitialFormValueContentBlocker, isExtendedFieldHidden, mapFromFormTranslationsIntoContentBlockerTemplate } from "../../../../../util/template/form.js";
import { clearFieldsByMergeStrategies } from "../../../../../util/template/merge-strategies.js";
import { getFieldNameTranslated } from "../../../../../util/template/translation.js";
import { mapSemverToVersion } from "../../../../../util/template/version.js";
import { ReactRouterPromptWrapper } from "../../../../third-party/react-router-prompt-wrapper.js";
import { RedirectModal } from "../../../redirect-modal.js";
import { PermissionCheck } from "../../user/permission.js";
import { UserTagLastEditors } from "../../user/user-tag-last-editors.js";
import { FormFieldTemplateDisplayName } from "../fields/display-name.js";
import { FormFieldTemplateEnabledWhenOneOfList } from "../fields/enabled-when-one-of-list.js";
import { FormFieldTemplateExtends } from "../fields/extends.js";
import { FormFieldTemplateHeadline } from "../fields/headline.js";
import { FormFieldTemplateIdentifier } from "../fields/identifier.js";
import { FormFieldTemplateIsDeleted } from "../fields/is-deleted.js";
import { FormFieldTemplateIsHidden } from "../fields/is-hidden.js";
import { FormFieldTemplateLogo } from "../fields/logo.js";
import { FormFieldTemplateMinRequiredRcbVersion } from "../fields/min-required-rcb-version.js";
import { FormFieldTemplateRecommendedWhenOneOfList } from "../fields/recommended-when-one-of-list.js";
import { TemplateMetaDataStatusTag } from "../fields/status.js";
import { FormFieldTemplateSubHeadline } from "../fields/sub-headline.js";
import { FormFieldTemplateSuccessorOfTemplate } from "../fields/successor-of-template.js";
import { FormFieldTemplateType } from "../fields/template-type.js";
import { FormFieldTemplateTier } from "../fields/tier.js";
import { FormFieldTemplateLanguages } from "../fields/translation/language-form.js";
import { FormFieldTemplateVersion } from "../fields/version.js";
import { FormFieldVisibilityWrapper } from "../formFieldVisibilityWrapper.js";
import { ExportModal } from "../migration/export-dialog.js";
/**
 * Form for ContentBlockerTemplate.
 */ const ContentBlockerTemplateForm = observer(({ contentBlockerTemplate, onSubmit, templateForm, isSubmitting = false, isLoading = false, isReadOnly = false, isModalView = false })=>{
    const { t } = useTranslation();
    // User settings for translations
    const { notificationStore, rcbAuthenticationStore, rcbUserSettingsStore } = useStores();
    const { settings: { translationSettings } } = rcbUserSettingsStore;
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const { Title } = Typography;
    const [hasChanges, setHasChanges] = useState(false);
    const [extendedId, setExtendedId] = useState(contentBlockerTemplate?.extends?.id);
    const useContentBlockerTemplateExtended = useContentBlockerTemplateById(extendedId);
    const { data: templateExtendedData } = useContentBlockerTemplateExtended;
    useEffect(()=>{
        const { isFetched, isError } = useContentBlockerTemplateExtended;
        if (templateExtendedData) {
            templateForm.setFieldValue(TemplateMetaDataField.Extends, templateExtendedData);
        } else if (isFetched && isError || extendedId === undefined) {
            templateForm.setFieldValue(TemplateMetaDataField.Extends, undefined);
        }
    }, [
        templateExtendedData,
        extendedId
    ]);
    const [isPopOverOpen, setPopOverOpen] = useState(false);
    const [shouldVersionIncrease, toggleVersionIncrease] = useShouldVersionIncrease(false);
    const onChangeExtendedTemplate = useCallback((template)=>{
        if (template && template.tier === ERcbTier.Pro) {
            templateForm.setFieldValue(TemplateMetaDataField.Tier, template.tier);
        }
        setExtendedId(template?.id);
    }, [
        templateForm,
        setExtendedId
    ]);
    const submitContentBlockerTemplate = useCallback(async (formTemplate)=>{
        const template = structuredClone(formTemplate);
        // Transform format from semver to our format
        template.minRequiredRcbVersion = mapSemverToVersion(template.minRequiredRcbVersion);
        // Transform Translations
        mapFromFormTranslationsIntoContentBlockerTemplate(template, templateForm);
        const currentVersion = Number(templateForm.getFieldValue(TemplateMetaDataField.Version));
        if (shouldVersionIncrease && !isNaN(currentVersion)) {
            templateForm.setFieldValue(TemplateMetaDataField.Version, currentVersion + 1);
            template.version = templateForm.getFieldValue(TemplateMetaDataField.Version);
        }
        clearFieldsByMergeStrategies(template);
        onSubmit?.(template, contentBlockerTemplate);
    }, [
        shouldVersionIncrease,
        contentBlockerTemplate
    ]);
    const initialValues = getInitialFormValueContentBlocker(contentBlockerTemplate);
    /** position: sticky; bottom: -25px; background: #f0f2f5; padding: 15px; */ const stickyButtonLayoutTop = useMemo(()=>{
        return {
            position: "sticky",
            top: isModalView ? "0px" : `-${spacing.layout.content.padding}px`,
            background: colors.main.background,
            zIndex: 2,
            borderBottom: "1px solid #d3d3d342",
            flexFlow: "column",
            flex: "1"
        };
    }, []);
    const stickyButtonLayoutBottom = useMemo(()=>{
        return {
            position: "sticky",
            bottom: isModalView ? "0px" : `-${spacing.layout.content.padding}px`,
            background: colors.main.background,
            paddingTop: `${spacing.layout.content.padding}px`,
            zIndex: 1,
            borderTop: "1px solid #d3d3d342"
        };
    }, []);
    const [hiddenIdentifier, setHiddenIdentifier] = useState([]);
    useEffect(()=>{
        setHiddenIdentifier([
            contentBlockerTemplate?.identifier
        ].filter(Boolean)); // TODO: refactor, use from form within FormFieldContentBlockerTemplateExtendsIdentifier
        if (contentBlockerTemplate) {
            templateForm.setFieldsValue(initialValues);
        }
    }, [
        contentBlockerTemplate
    ]);
    const [activeTemplates, setActiveTemplates] = useState([]);
    const [deletedTemplates, setDeletedTemplates] = useState([]);
    const useContentBlockerMetaData = useContentBlockerTemplateMetaData(true, true, undefined, true);
    const { data: blockerMetaData } = useContentBlockerMetaData;
    useEffect(()=>{
        setActiveTemplates(blockerMetaData?.filter((t)=>!t.isDeleted));
        setDeletedTemplates(blockerMetaData?.filter((t)=>t.isDeleted));
    }, [
        blockerMetaData
    ]);
    const formTitle = contentBlockerTemplate?.id ? `${contentBlockerTemplate.headline} ${contentBlockerTemplate.subHeadline ? contentBlockerTemplate.subHeadline : ""}` : t("Create Content Blocker Template");
    const [exportOpen, setExportOpen] = useState(false);
    const [exportData, setExportData] = useState(undefined);
    const [importData, setImportData] = useState(undefined);
    const [redirectOpen, setRedirectOpen] = useState(false);
    const [redirectToPath, setRedirectToPath] = useState();
    useEffect(()=>{
        if (importData) {
            const importable = JSON.parse(importData);
            const givenIdentifier = templateForm.getFieldValue(TemplateMetaDataField.Identifier);
            if (!!givenIdentifier && importable.identifier !== givenIdentifier) {
                notificationStore.add({
                    type: "warning",
                    message: t("Import canceled! The provided template identifier does not match the current template. Please ensure you are using a matching template."),
                    closable: true
                });
            } else if (!givenIdentifier && activeTemplates.findIndex((t)=>t.identifier === importable.identifier) >= 0) {
                setRedirectToPath(`/rcb/content-blocker/edit/${importable.identifier}`);
            } else {
                prepareImportDataContentBlocker(importable);
                templateForm.setFieldsValue(importable);
                if (importable.extendsIdentifier) {
                    onChangeExtendedTemplate(activeTemplates.find((t)=>t.identifier === importable.extendsIdentifier));
                }
                notificationStore.add({
                    type: "success",
                    message: t("Import completed successfully!"),
                    duration: 5
                });
                setHasChanges(true);
                templateForm.validateFields();
            }
            setImportData(undefined);
        }
    }, [
        importData
    ]);
    useEffect(()=>{
        if (redirectToPath) {
            setRedirectOpen(true);
        }
    }, [
        redirectToPath
    ]);
    const translatorFieldPermissions = {
        whenRole: EUserRole.Translator,
        roleWhiteList: TRANSLATABLE_CONTENT_BLOCKER_TEMPLATE_ATTRIBUTES_LIST,
        strategy: "any"
    };
    const extended = templateForm.getFieldValue(TemplateMetaDataField.Extends);
    const visualType = templateForm.getFieldValue(ContentBlockerField.VisualType);
    const visualContentType = templateForm.getFieldValue(ContentBlockerField.VisualContentType);
    const isHeroContentType = (visualType === EContentBlockerVisualType.Hero || extended?.[ContentBlockerField.VisualType] === EContentBlockerVisualType.Hero) && (VISUAL_CONTENT_HERO_TYPES.includes(visualContentType) || VISUAL_CONTENT_HERO_TYPES.includes(extended?.[ContentBlockerField.VisualContentType]));
    useFormEvent(EFormEvent.TRANSLATION_FLAGS_CHANGED, ()=>{
        if (!hasChanges) {
            setHasChanges(true);
        }
    });
    return /*#__PURE__*/ _jsxs(Spin, {
        spinning: isLoading,
        children: [
            /*#__PURE__*/ _jsx(ReactRouterPromptWrapper, {
                when: hasChanges && !isSubmitting
            }),
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: formTitle
                })
            }),
            /*#__PURE__*/ _jsxs(Row, {
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        style: {
                            flex: "1"
                        },
                        children: /*#__PURE__*/ _jsx(Title, {
                            level: 3,
                            style: {
                                paddingBottom: "0.2rem"
                            },
                            children: formTitle
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Col, {
                        children: [
                            /*#__PURE__*/ _jsx(PermissionCheck, {
                                minRole: EUserRole.Creator,
                                children: /*#__PURE__*/ _jsx(Button, {
                                    icon: /*#__PURE__*/ _jsx(FileZipOutlined, {}),
                                    type: "link",
                                    onClick: ()=>{
                                        const formData = prepareExportDataContentBlocker(templateForm.getFieldsValue());
                                        setExportData(formData);
                                        setExportOpen(true);
                                    },
                                    children: t("Import/Export")
                                })
                            }),
                            contentBlockerTemplate?.id && /*#__PURE__*/ _jsx(TemplateMetaDataStatusTag, {
                                toolTip: `Id: ${contentBlockerTemplate.id}`,
                                status: contentBlockerTemplate.status
                            }),
                            /*#__PURE__*/ _jsx(UserTagLastEditors, {
                                lastKnownEditor: contentBlockerTemplate?.lastEditedBy,
                                templateId: contentBlockerTemplate?.id,
                                type: "Content-Blocker",
                                disabled: !contentBlockerTemplate?.lastEditedBy
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsxs(Form, {
                    disabled: isLoading || isReadOnly,
                    name: "cb-form",
                    form: templateForm,
                    initialValues: initialValues,
                    onFinish: (values)=>submitContentBlockerTemplate(values),
                    ...templateFormDefaultProps,
                    onValuesChange: (changedValue, allValues)=>{
                        if (!hasChanges) {
                            setHasChanges(true); // once is just fine
                        }
                    },
                    onFinishFailed: (event)=>{
                        for (const error of event.errorFields){
                            notificationStore.add({
                                type: "error",
                                message: error.errors.join(", "),
                                title: getFieldNameTranslated(String(error.name), t)
                            });
                        }
                    },
                    scrollToFirstError: true,
                    children: [
                        /*#__PURE__*/ _jsx(Row, {
                            style: translationSettings.isStickyMode ? stickyButtonLayoutTop : {
                                flexFlow: "column"
                            },
                            children: /*#__PURE__*/ _jsx(Row, {
                                style: {
                                    flexFlow: "inherit"
                                },
                                children: /*#__PURE__*/ _jsx(FormFieldTemplateLanguages, {})
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateType, {
                            type: "Content-Blocker"
                        }),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                TemplateMetaDataField.Version,
                                TemplateMetaDataField.ExtendsIdentifier,
                                TemplateMetaDataField.Identifier,
                                TemplateMetaDataField.Logo,
                                TemplateMetaDataField.Headline,
                                TemplateMetaDataField.SubHeadline,
                                TemplateMetaDataField.SuccessorOfIdentifier
                            ],
                            permissionCheck: translatorFieldPermissions,
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("Meta data")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateVersion, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateExtends, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateExtendsIdentifier, {
                            onChangeExtendedTemplate: onChangeExtendedTemplate,
                            hiddenIdentifier: hiddenIdentifier,
                            templates: activeTemplates
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateIdentifier, {
                            enabled: !contentBlockerTemplate?.id
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateLogo, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateHeadline, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateSubHeadline, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateSuccessorOfTemplate, {
                            activeTemplates: activeTemplates,
                            deletedTemplates: deletedTemplates,
                            hiddenIdentifier: hiddenIdentifier
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateIsDeleted, {}),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                TemplateMetaDataField.Tier,
                                TemplateMetaDataField.MinRequiredRcbVersion,
                                TemplateMetaDataField.EnabledWhenOneOf,
                                TemplateMetaDataField.RecommendedWhenOneOf,
                                TemplateMetaDataField.IsHidden
                            ],
                            permissionCheck: translatorFieldPermissions,
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("Conditions")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateTier, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateMinRequiredRcbVersion, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateEnabledWhenOneOfList, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateRecommendedWhenOneOfList, {}),
                        /*#__PURE__*/ _jsx(FormFieldTemplateIsHidden, {}),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                TemplateMetaDataField.Name,
                                ContentBlockerField.Description
                            ],
                            permissionCheck: translatorFieldPermissions,
                            forceHiddenFormCallback: areExtendedFieldsHidden([
                                TemplateMetaDataField.Name,
                                ContentBlockerField.Description
                            ], hasCreatorRole),
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("General")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldTemplateDisplayName, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateDescription, {}),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                ContentBlockerField.RuleGroups,
                                ContentBlockerField.Rules,
                                ContentBlockerField.RuleNotice,
                                ContentBlockerField.ExampleEmbedCodes,
                                ContentBlockerField.ServiceTemplateIdentifiers
                            ],
                            permissionCheck: translatorFieldPermissions,
                            forceHiddenFormCallback: isExtendedFieldHidden(ContentBlockerField.RuleNotice, hasCreatorRole),
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("Technical Definition")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateRuleGroups, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateRules, {}),
                        /*#__PURE__*/ _jsx(FormFieldServiceTemplateRuleNotice, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateExampleEmbedCodeList, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateConnectedServices, {}),
                        /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
                            fieldNames: [
                                ContentBlockerField.IsVisual,
                                ContentBlockerField.VisualType,
                                ContentBlockerField.VisualContentType,
                                ContentBlockerField.VisualHeroButtonText,
                                ContentBlockerField.IsVisualDarkMode,
                                ContentBlockerField.VisualBlur,
                                ContentBlockerField.ShouldForceToShowVisual
                            ],
                            permissionCheck: translatorFieldPermissions,
                            forceHidden: !isHeroContentType && EUserRole.Translator === rcbAuthenticationStore.session?.user?.role,
                            forceHiddenFormCallback: isExtendedFieldHidden(ContentBlockerField.VisualHeroButtonText, hasCreatorRole),
                            children: /*#__PURE__*/ _jsx(Title, {
                                level: 4,
                                children: t("Visual")
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateIsVisual, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateVisualType, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateVisualContentType, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateVisualHeroButtonText, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateIsVisualDarkMode, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateVisualBlur, {}),
                        /*#__PURE__*/ _jsx(FormFieldContentBlockerTemplateShouldForceToShowVisual, {})
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Row, {
                style: stickyButtonLayoutBottom,
                justify: "center",
                align: "middle",
                children: contentBlockerTemplate?.id ? /*#__PURE__*/ _jsx(Popover, {
                    trigger: "click",
                    placement: "top",
                    title: t("Save template"),
                    open: isPopOverOpen,
                    onOpenChange: (newOpenState)=>setPopOverOpen(newOpenState),
                    content: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(PermissionCheck, {
                                minRole: EUserRole.Creator,
                                children: [
                                    /*#__PURE__*/ _jsx(Row, {
                                        children: /*#__PURE__*/ _jsx(Checkbox, {
                                            checked: shouldVersionIncrease,
                                            onChange: toggleVersionIncrease,
                                            children: t("Notify users")
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Row, {
                                        children: /*#__PURE__*/ _jsx("br", {})
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Row, {
                                children: /*#__PURE__*/ _jsxs(Space, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            onClick: ()=>setPopOverOpen(false),
                                            children: t("Cancel")
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            disabled: !shouldVersionIncrease && !hasChanges,
                                            style: {
                                                width: "10rem"
                                            },
                                            type: "primary",
                                            onClick: ()=>{
                                                templateForm.submit();
                                                setPopOverOpen(false);
                                            },
                                            loading: isLoading,
                                            children: shouldVersionIncrease ? t("Save & Notify") : t("Save")
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(Button, {
                        type: "primary",
                        children: t("Save")
                    })
                }) : /*#__PURE__*/ _jsx(Button, {
                    disabled: !hasChanges,
                    type: "primary",
                    onClick: ()=>templateForm.submit(),
                    loading: isLoading,
                    children: t("Create")
                })
            }),
            exportOpen ? /*#__PURE__*/ _jsx(ExportModal, {
                open: exportOpen,
                onCancel: ()=>{
                    setExportOpen(false);
                },
                exportableData: exportData,
                onSubmit: (importData)=>{
                    setExportOpen(false);
                    setImportData(importData);
                }
            }) : null,
            /*#__PURE__*/ _jsx(RedirectModal, {
                redirectToPath: redirectToPath,
                open: redirectOpen,
                onCancel: ()=>{
                    setRedirectOpen(false);
                    setRedirectToPath(undefined);
                },
                title: t("Content-Blocker already exists"),
                okText: t("Redirect"),
                children: t("Import canceled. The template to import already exists. Would you like to be taken to it?")
            })
        ]
    });
});
export { ContentBlockerTemplateForm };
